/**
 * src/reporting/reporter.ts — client-side error + feedback reporting.
 *
 * Captures uncaught JS errors (window.onerror), unhandled promise rejections,
 * and React render errors (via ErrorBoundary.componentDidCatch) and writes
 * them to the Harper ErrorLog table. When Harper is unreachable, payloads
 * queue in localStorage and flush on the next `online` event or install.
 *
 * Feedback follows the same path: submit goes direct, failures fall back to
 * the same localStorage queue mechanism.
 *
 * Installation happens once at app boot via <ReporterInstaller/>, which wires
 * the current auth + WS-log getters into the module. Before install, reports
 * still queue — they just can't attach user context.
 */

import { createErrorLog, type CreateErrorLogPayload } from '../api/errorLogApi';
import { createFeedback, type CreateFeedbackPayload } from '../api/feedbackApi';
import type { WsLogEntry } from '../context/HarperWSContext';

export interface ReporterUserContext {
  customer_id: string | null;
  customer_email: string | null;
  role: string | null;
}

type UserGetter = () => ReporterUserContext;
type WsLogGetter = () => WsLogEntry[];

const ERROR_QUEUE_KEY = 'attic.errorQueue.v1';
const FEEDBACK_QUEUE_KEY = 'attic.feedbackQueue.v1';

let _getUser: UserGetter = () => ({ customer_id: null, customer_email: null, role: null });
let _getWsLog: WsLogGetter = () => [];
let _installed = false;
// Re-entry guard: prevents an error inside reportError from triggering more
// error captures (infinite loop) via window.onerror/unhandledrejection.
let _reporting = false;

/* ------------------------------------------------------------------ */
/*  Queue helpers                                                      */
/* ------------------------------------------------------------------ */

function readQueue<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue<T>(key: string, items: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // quota exceeded / disabled storage — drop silently; we don't want a
    // reporting failure to take down the app.
  }
}

function enqueue<T>(key: string, item: T): void {
  const items = readQueue<T>(key);
  items.push(item);
  // Cap queues so a long offline stretch doesn't blow up localStorage.
  const MAX = 50;
  if (items.length > MAX) items.splice(0, items.length - MAX);
  writeQueue(key, items);
}

/* ------------------------------------------------------------------ */
/*  Public API — report                                                */
/* ------------------------------------------------------------------ */

export interface CapturedError {
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
}

/**
 * Capture and persist a client-side error. Safe to call at any point — if the
 * reporter hasn't been installed yet, the payload queues without user context.
 */
export async function reportError(err: CapturedError): Promise<void> {
  if (_reporting) return;
  _reporting = true;
  try {
    const user = _getUser();
    const payload: CreateErrorLogPayload = {
      customer_email: user.customer_email,
      role: user.role,
      route: typeof window !== 'undefined' ? window.location.hash || window.location.pathname : '',
      message: err.message || 'Unknown error',
      stack: err.stack ?? '',
      source: err.source ?? '',
      line: err.line ?? null,
      column: err.column ?? null,
      ws_log: safeStringify(_getWsLog()),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
    try {
      await createErrorLog(payload);
    } catch {
      enqueue(ERROR_QUEUE_KEY, payload);
    }
  } catch {
    // Swallow — reporting must never throw.
  } finally {
    _reporting = false;
  }
}

/**
 * Submit user feedback. Returns true on success (direct or queued for later).
 */
export async function submitFeedback(textBody: string): Promise<boolean> {
  const payload: CreateFeedbackPayload = {
    route: typeof window !== 'undefined' ? window.location.hash || window.location.pathname : '',
    text_body: textBody,
    ws_log: safeStringify(_getWsLog()),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
  try {
    await createFeedback(payload);
    return true;
  } catch {
    enqueue(FEEDBACK_QUEUE_KEY, payload);
    return true; // queued locally — still a success from the user's perspective
  }
}

/* ------------------------------------------------------------------ */
/*  Queue flush                                                        */
/* ------------------------------------------------------------------ */

export async function flushQueues(): Promise<void> {
  // Errors first — they're the more time-sensitive signal.
  const errors = readQueue<CreateErrorLogPayload>(ERROR_QUEUE_KEY);
  const remainingErrors: CreateErrorLogPayload[] = [];
  for (const payload of errors) {
    try {
      await createErrorLog(payload);
    } catch {
      remainingErrors.push(payload);
    }
  }
  writeQueue(ERROR_QUEUE_KEY, remainingErrors);

  const feedbacks = readQueue<CreateFeedbackPayload>(FEEDBACK_QUEUE_KEY);
  const remainingFeedbacks: CreateFeedbackPayload[] = [];
  for (const payload of feedbacks) {
    try {
      await createFeedback(payload);
    } catch {
      remainingFeedbacks.push(payload);
    }
  }
  writeQueue(FEEDBACK_QUEUE_KEY, remainingFeedbacks);
}

/* ------------------------------------------------------------------ */
/*  Install                                                            */
/* ------------------------------------------------------------------ */

export interface InstallOptions {
  getUser: UserGetter;
  getWsLog: WsLogGetter;
}

export function installReporter(opts: InstallOptions): void {
  _getUser = opts.getUser;
  _getWsLog = opts.getWsLog;
  if (_installed) return;
  _installed = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      // event.error is often the Error; event.message is the string form.
      const err: CapturedError = {
        message: event.error?.message ?? event.message ?? 'Unknown error',
        stack: event.error?.stack,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      };
      void reportError(err);
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const err: CapturedError = {
        message: reason?.message ?? (typeof reason === 'string' ? reason : 'Unhandled promise rejection'),
        stack: reason?.stack,
      };
      void reportError(err);
    });

    window.addEventListener('online', () => { void flushQueues(); });
  }

  // Best-effort flush on install (user was offline last session, now loaded).
  void flushQueues();
}

/* ------------------------------------------------------------------ */
/*  Utils                                                              */
/* ------------------------------------------------------------------ */

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '[]';
  }
}
